const PRODUCT_FILES_MODAL_REOPEN_INTENT_KEY =
    'product-files-modal-reopen-intent';

interface ProductFilesModalReopenIntent {
    productID: number;
    professionalID: number;
    pathname: string;
    pageLoadID: string;
}

const canUseSessionStorage = () =>
    typeof window !== 'undefined' &&
    typeof window.sessionStorage !== 'undefined';

/**
 * Identifies the current page load so auth-triggered reloads can be distinguished
 * from same-page state updates that happen before navigation completes.
 */
export const getCurrentProductFilesModalPageLoadID = () => {
    if (typeof window === 'undefined') return 'server';

    return String(window.performance?.timeOrigin ?? 0);
};

/**
 * Stores a short-lived intent to reopen the product files modal after auth.
 */
export const storeProductFilesModalReopenIntent = (
    intent: ProductFilesModalReopenIntent,
) => {
    if (!canUseSessionStorage()) return;

    window.sessionStorage.setItem(
        PRODUCT_FILES_MODAL_REOPEN_INTENT_KEY,
        JSON.stringify(intent),
    );
};

/**
 * Removes any saved product files modal reopen intent.
 */
export const clearProductFilesModalReopenIntent = () => {
    if (!canUseSessionStorage()) return;

    window.sessionStorage.removeItem(PRODUCT_FILES_MODAL_REOPEN_INTENT_KEY);
};

/**
 * Consumes a saved reopen intent when it matches the current PDP context.
 */
export const consumeProductFilesModalReopenIntent = (
    current: ProductFilesModalReopenIntent,
): { shouldReopen: boolean; shouldClear: boolean } => {
    if (!canUseSessionStorage()) {
        return { shouldReopen: false, shouldClear: false };
    }

    const rawIntent = window.sessionStorage.getItem(
        PRODUCT_FILES_MODAL_REOPEN_INTENT_KEY,
    );
    if (!rawIntent) {
        return { shouldReopen: false, shouldClear: false };
    }

    try {
        const savedIntent = JSON.parse(
            rawIntent,
        ) as ProductFilesModalReopenIntent;
        const matchesCurrentPage =
            savedIntent.pathname === current.pathname &&
            savedIntent.productID === current.productID &&
            savedIntent.professionalID === current.professionalID;

        if (!matchesCurrentPage) {
            clearProductFilesModalReopenIntent();
            return { shouldReopen: false, shouldClear: true };
        }

        if (savedIntent.pageLoadID === current.pageLoadID) {
            return { shouldReopen: false, shouldClear: false };
        }

        clearProductFilesModalReopenIntent();
        return { shouldReopen: true, shouldClear: true };
    } catch {
        clearProductFilesModalReopenIntent();
        return { shouldReopen: false, shouldClear: true };
    }
};
